import { useEffect, useMemo, useState } from 'react'
import { Alert, Button, DatePicker, Input, Row, Col, Select, Table, Tag, Modal } from 'antd'
import { HistoryOutlined } from '@ant-design/icons'
import EntityLink from '../../components/Links/EntityLink'
import { activitySubjectPath, canOpenActivitySubject, userPath } from '../../utilities/entityPaths'
import { useEntityLinks } from '../../hooks/useEntityLinks'
import dayjs from 'dayjs'
import http from '../../store/services/http-common'
import { formatApiError } from '../../utilities/apiErrorMessages'
import { coalesceAsync } from '../../utilities/coalesceAsync'
import useTranslation from '../../localization/useTranslation'
import { translateDeliveryTypeName, translateUserTypeName } from '../../utilities/translateCatalog'
import config from '../../config'
import ToothPicker from '../../components/ToothPicker/ToothPicker'
import ListPageShell from '../../components/Layout/ListPageShell'

const operationColor = (operation) => {
  switch ((operation || '').toUpperCase()) {
    case 'CREATE':
    case 'PAID_INVOICE':
    case 'SEND_EMAIL':
    case 'SEND_WHATSAPP':
      return 'green'
    case 'UPDATE':
    case 'DOWNLOAD_PDF':
      return 'blue'
    case 'DELETE':
    case 'UNDO_MARK_PAID':
    case 'CANCEL':
    case 'REFUND_INVOICE':
      return 'red'
    case 'LOGIN':
    case 'LOGOUT':
    case 'PASSWORD_RESET':
    case 'PASSWORD_RESET_REQUEST':
      return 'purple'
    default:
      return 'default'
  }
}

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)
const EXCLUDED_DIFF_FIELDS = new Set(['vendor', 'channel'])

const formatHumanDateTime = (value) => {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  const isStrictDateLike =
    /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ||
    /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2})?(\.\d+)?(Z|[+-]\d{2}:?\d{2})?$/.test(trimmed)
  if (!isStrictDateLike) return null
  const parsed = dayjs(trimmed)
  if (!parsed.isValid()) return null
  return parsed.format('YYYY-MM-DD hh:mm:ss A')
}

const toComparable = (value) => {
  if (value === undefined) return null
  return value
}

const stringifyValue = (value) => {
  if (value === null || value === undefined) return '-'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (typeof value === 'string') {
    const humanDate = formatHumanDateTime(value)
    if (humanDate) return humanDate
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value)
  }
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

const isPermissionField = (path) => {
  if (!path) return false
  return path === 'permissions' || path.endsWith('.permissions')
}

const isDoctorsField = (path) => {
  if (!path) return false
  return path === 'doctors' || path.endsWith('.doctors')
}

const isDoctorField = (path) => {
  if (!path) return false
  return path === 'doctor' || path.endsWith('.doctor')
}

const isTechnicianField = (path) => {
  if (!path) return false
  return path === 'technician' || path.endsWith('.technician')
}

const isCourierField = (path) => {
  if (!path) return false
  return path === 'courier' || path.endsWith('.courier')
}

const isColorField = (path) => {
  if (!path) return false
  return path === 'color' || path.endsWith('.color')
}

const isUserTypeField = (path) => {
  if (!path) return false
  return path === 'user_type' || path.endsWith('.user_type') || path === 'userType' || path.endsWith('.userType')
}

const isClientField = (path) => {
  if (!path) return false
  return path === 'client' || path.endsWith('.client')
}

const isDeliveryCenterField = (path) => {
  if (!path) return false
  return path === 'delivery_center' || path.endsWith('.delivery_center')
}

const isAttachmentsField = (path) => {
  if (!path) return false
  return path === 'attachments' || path.endsWith('.attachments')
}

const isUnitsField = (path) => {
  if (!path) return false
  return path === 'units' || path.endsWith('.units')
}

const isOrdersField = (path) => {
  if (!path) return false
  return path === 'orders' || path.endsWith('.orders')
}

const isProductField = (path) => {
  if (!path) return false
  return path === 'product' || path.endsWith('.product')
}

const isDeliveryTypeField = (path) => {
  if (!path) return false
  return path === 'delivery_type' || path.endsWith('.delivery_type')
}

const toPermissionLabels = (value) => {
  if (!Array.isArray(value)) return null

  const labels = value
    .map((item) => {
      if (typeof item === 'string') return item
      if (item && typeof item === 'object' && typeof item.name === 'string') return item.name
      return null
    })
    .filter(Boolean)

  if (!labels.length) return null
  return labels.join('\n')
}

const toDoctorNames = (value) => {
  if (!Array.isArray(value)) return null

  const names = value
    .map((item) => {
      if (typeof item === 'string') return item
      if (item && typeof item === 'object' && typeof item.name === 'string') return item.name
      return null
    })
    .filter(Boolean)

  if (!names.length) return null
  return names
}

const toUserTypeName = (value) => {
  if (value === null || value === undefined) return null
  if (typeof value === 'string') return value
  if (value && typeof value === 'object' && typeof value.name === 'string') return value.name
  return null
}

const toClientName = (value) => {
  if (value === null || value === undefined) return null
  if (typeof value === 'string') return value
  if (value && typeof value === 'object' && typeof value.name === 'string') return value.name
  return null
}

const toNamedLabel = (value) => {
  if (value === null || value === undefined) return null
  if (typeof value === 'string') return value
  if (value && typeof value === 'object' && typeof value.name === 'string') return value.name
  return null
}

const toColorDisplayData = (value) => {
  if (!value || typeof value !== 'object') return null
  const name = typeof value.name === 'string' ? value.name : null
  const code = typeof value.code === 'string' ? value.code : null
  if (!name && !code) return null
  return {
    __type: 'color_display',
    name: name ?? '-',
    code: code ?? null,
  }
}

const toFormulaTypeDetailsData = (value) => {
  if (!value || typeof value !== 'object') return null
  if (typeof value.name !== 'string' && typeof value.description !== 'string') return null

  return {
    __type: 'formula_type_details',
    name: value?.name ?? '-',
    description: value?.description ?? '-',
    sellingPrice: value?.selling_cost ?? value?.selling_price ?? value?.selling_price_per_unit ?? '-',
    purchasePrice: value?.purchase_cost ?? value?.technician_cost ?? value?.purchase_price_per_unit ?? '-',
  }
}

const toAttachmentNames = (value) => {
  if (!Array.isArray(value)) return null

  const names = value
    .map((item) => {
      if (typeof item === 'string') return item
      if (item && typeof item === 'object' && typeof item.name === 'string') return item.name
      return null
    })
    .filter(Boolean)

  if (!names.length) return null
  return names
}

const toAttachmentItems = (value) => {
  if (!Array.isArray(value)) return null

  const items = value
    .map((item) => {
      if (typeof item === 'string') {
        return { name: item, path: null, type: null, extension: null }
      }
      if (item && typeof item === 'object') {
        const name = typeof item.name === 'string' ? item.name : null
        const path = typeof item.path === 'string' ? item.path : null
        const type = typeof item.type === 'string' ? item.type : null
        const extension = typeof item.extension === 'string' ? item.extension : null
        if (name || path) {
          return { name: name ?? path, path, type, extension }
        }
      }
      return null
    })
    .filter(Boolean)

  if (!items.length) return null
  return items
}

const isImageAttachment = (item) => {
  const type = String(item?.type ?? '').toLowerCase()
  const ext = String(item?.extension ?? '').toLowerCase()
  return type === 'image' || ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg'].includes(ext)
}

const attachmentUrl = (path) => {
  if (!path) return null
  if (path.startsWith('http://') || path.startsWith('https://')) return path

  let origin
  try {
    origin = new URL(config.apiUrl).origin
  } catch {
    origin = window.location.origin
  }

  const trimmed = path.replace(/^\//, '')
  return `${origin}/storage/${encodeURI(trimmed)}`
}

const toUnitsPreviewData = (value) => {
  if (!Array.isArray(value)) return null

  const cards = value
    .map((item) => {
      if (!item || typeof item !== 'object') return null

      const unitOrder = item?.unit_type?.order
      if (unitOrder == null) return null

      const ft = item?.formula_type ?? item?.formula_type
      const selling = ft?.selling_cost ?? ft?.selling_price ?? ft?.selling_price_per_unit
      const purchase = ft?.purchase_cost ?? ft?.technician_cost ?? ft?.purchase_price_per_unit
      const override = item?.unit_price_override
      const unitPrice = override != null && override !== '' ? Number(override) : Number(selling ?? purchase ?? 0)

      return {
        unit: Number(unitOrder),
        formulaTypeName: item?.formula_type?.name ?? '-',
        colorName: item?.color?.name ?? '-',
        priceLabel: Number.isFinite(unitPrice) ? String(unitPrice) : '-',
      }
    })
    .filter(Boolean)
    .sort((a, b) => a.unit - b.unit)

  if (!cards.length) return null
  return {
    __type: 'units_preview',
    selected: cards.map((c) => c.unit),
    cards,
  }
}

const toOrderLabelsData = (value) => {
  if (!Array.isArray(value)) return null

  const items = value
    .map((item) => {
      if (!item || typeof item !== 'object') return null
      const orderRef = item.reference ?? (item.id != null ? `#${item.id}` : '-')
      const clientName = item.client_name ?? item.clinic_name ?? '-'
      const doctorName = item.doctor_name ?? '-'
      const patientName = item.patient_name ?? '-'
      return {
        key: String(item.id ?? orderRef),
        orderRef,
        clientName,
        doctorName,
        patientName,
      }
    })
    .filter(Boolean)

  if (!items.length) return null
  return {
    __type: 'order_labels',
    items,
  }
}

const renderCompactUnitCard = (card) => (
  <div
    style={{
      minWidth: 130,
      border: '1px solid #d9d9d9',
      borderRadius: 8,
      padding: '6px 8px',
      background: '#fafafa',
    }}
  >
    <div style={{ fontWeight: 600, marginBottom: 4, fontSize: 12 }}>
      Unit {card.unit}
    </div>
    <div style={{ fontSize: 11, color: '#555' }}>
      Formula type: {card.formulaTypeName ?? '-'}
    </div>
    <div style={{ fontSize: 11, color: '#555' }}>
      Color: {card.colorName ?? '-'}
    </div>
    <div style={{ fontSize: 11, color: '#555' }}>
      Price: {card.priceLabel ?? '-'}
    </div>
  </div>
)

const humanizeFieldLabel = (field) => {
  if (field === null || field === undefined) return '-'

  return String(field)
    .replace(/\./g, ' ')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase())
}

const toFieldTranslationKey = (field) =>
  String(field ?? '')
    .trim()
    .toLowerCase()
    .replace(/[.\s]+/g, '_')

const normalizeDoctorNames = (value) => {
  const names = toDoctorNames(value)
  if (!names) return []
  return Array.from(new Set(names.map((name) => String(name).trim()).filter(Boolean))).sort((a, b) =>
    a.localeCompare(b)
  )
}

const areDoctorNamesEqual = (oldValue, newValue) => {
  const oldNames = normalizeDoctorNames(oldValue)
  const newNames = normalizeDoctorNames(newValue)
  if (oldNames.length !== newNames.length) return false
  for (let i = 0; i < oldNames.length; i += 1) {
    if (oldNames[i] !== newNames[i]) return false
  }
  return true
}

const formatDiffValue = (path, value, language = 'ar') => {
  if (isPermissionField(path)) {
    const labels = toPermissionLabels(value)
    if (labels !== null) return labels
  }
  if (isUserTypeField(path)) {
    const typeName = toUserTypeName(value)
    if (typeName !== null) return translateUserTypeName(typeName, language)
  }
  if (isClientField(path)) {
    const clientName = toClientName(value)
    if (clientName !== null) return clientName
  }
  if (isDoctorField(path)) {
    const doctorName = toNamedLabel(value)
    if (doctorName !== null) return doctorName
  }
  if (isTechnicianField(path)) {
    const technicianName = toNamedLabel(value)
    if (technicianName !== null) return technicianName
  }
  if (isCourierField(path)) {
    const courierName = toNamedLabel(value)
    if (courierName !== null) return courierName
  }
  if (isDeliveryCenterField(path)) {
    const centerName = toNamedLabel(value)
    if (centerName !== null) return centerName
  }
  if (isProductField(path)) {
    const productName = toNamedLabel(value)
    if (productName !== null) return productName
  }
  if (isDeliveryTypeField(path)) {
    if (value && typeof value === 'object') {
      return translateDeliveryTypeName(value, language)
    }
    const deliveryTypeName = toNamedLabel(value)
    if (deliveryTypeName !== null) return translateDeliveryTypeName(deliveryTypeName, language)
  }

  if (isColorField(path)) {
    const colorData = toColorDisplayData(value)
    if (colorData !== null) return colorData
  }
  if (isAttachmentsField(path)) {
    const items = toAttachmentItems(value)
    if (items !== null) return { __type: 'attachment_items', items }
    const names = toAttachmentNames(value)
    if (names !== null) return { __type: 'attachment_labels', items: names.map((name) => ({ name, path: null, type: null, extension: null })) }
  }
  if (isUnitsField(path)) {
    const unitsPreview = toUnitsPreviewData(value)
    if (unitsPreview !== null) return unitsPreview
  }
  if (isOrdersField(path)) {
    const orderLabels = toOrderLabelsData(value)
    if (orderLabels !== null) return orderLabels
  }
  if (isDoctorsField(path)) {
    const names = toDoctorNames(value)
    if (names !== null) return { __type: 'doctor_labels', items: names }
  }
  return stringifyValue(value)
}

const diffObjects = (oldValue, newValue, path = '', acc = [], language = 'ar') => {
  const oldVal = toComparable(oldValue)
  const newVal = toComparable(newValue)

  if (isPlainObject(oldVal) && isPlainObject(newVal)) {
    const keys = Array.from(new Set([...Object.keys(oldVal), ...Object.keys(newVal)]))
    keys.forEach((key) => {
      if (EXCLUDED_DIFF_FIELDS.has(key)) return
      const nextPath = path ? `${path}.${key}` : key
      diffObjects(oldVal[key], newVal[key], nextPath, acc, language)
    })
    return acc
  }

  const oldJson = JSON.stringify(oldVal)
  const newJson = JSON.stringify(newVal)
  if (oldJson !== newJson) {
    if (isDoctorsField(path) && areDoctorNamesEqual(oldVal, newVal)) {
      return acc
    }

    acc.push({
      key: path || 'value',
      field: path || 'value',
      old: formatDiffValue(path, oldVal, language),
      new: formatDiffValue(path, newVal, language),
    })
  }

  return acc
}

const Activities = () => {
  const { t, language } = useTranslation()
  const entityLinks = useEntityLinks()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [rows, setRows] = useState([])
  const [total, setTotal] = useState(0)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [filters, setFilters] = useState({
    name: '',
    entity: '',
    operation: undefined,
    dateRange: null,
  })
  const [propertiesModalOpen, setPropertiesModalOpen] = useState(false)
  const [selectedActivity, setSelectedActivity] = useState(null)

  const fetchActivities = async () => {
    setLoading(true)
    setError('')

    try {
      const params = {
        page: currentPage,
        per_page: pageSize,
      }

      if (filters.name) params.name = filters.name
      if (filters.entity) params.entity = filters.entity
      if (filters.operation) params.operation = filters.operation
      if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
        params.start_date = filters.dateRange[0].format('YYYY-MM-DD')
        params.end_date = filters.dateRange[1].format('YYYY-MM-DD')
      }

      const res = await coalesceAsync(
        `get-activities:${JSON.stringify(params)}`,
        () => http.get('/activities', { params }),
        { ttlMs: 1500 }
      )
      const payload = res?.data?.data
      const data = Array.isArray(payload?.data) ? payload.data : []
      setRows(data)
      setTotal(Number(payload?.total ?? data.length))
    } catch (e) {
      setError(formatApiError(e, t('errors.fetchFailed') || 'Failed to fetch activities'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchActivities()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, filters.name, filters.entity, filters.operation, filters.dateRange])

  const entityOptions = useMemo(() => {
    const entities = Array.from(new Set((rows || []).map((r) => r.entity).filter(Boolean)))
    return entities.map((entity) => ({ label: entity, value: entity }))
  }, [rows])

  const columns = [
    {
      title: t('activities.table.entity_affected') || 'Entity affected',
      dataIndex: 'entity_affected',
      key: 'entity_affected',
      render: (_, record) => (
        <EntityLink
          to={activitySubjectPath(record.entity, record.entity_id)}
          enabled={canOpenActivitySubject(record.entity, entityLinks)}
        >
          {record.entity_affected || '-'}
        </EntityLink>
      ),
    },
    {
      title: t('activities.table.user') || 'User name',
      dataIndex: ['user', 'name'],
      key: 'user_name',
      render: (_, record) => {
        if (!record?.user?.id) return record?.user?.name || '-'
        return (
          <EntityLink to={userPath(record.user.id)} enabled={entityLinks.canOpenUser}>
            {record.user.name}
          </EntityLink>
        )
      },
    },
    {
      title: t('activities.table.operation') || 'Operation',
      dataIndex: 'operation',
      key: 'operation',
      render: (value) => <Tag color={operationColor(value)}>{value || '-'}</Tag>,
    },
    {
      title: t('activities.table.ip') || 'Device IP',
      dataIndex: 'device_ip',
      key: 'device_ip',
      render: (value) => value || '-',
    },
    {
      title: t('activities.table.date') || 'Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (value) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '-'),
    },
    {
      title: t('activities.table.properties') || 'Properties',
      key: 'properties_action',
      render: (_, record) => (
        <Button
          size="small"
          onClick={() => {
            setSelectedActivity(record ?? null)
            setPropertiesModalOpen(true)
          }}
        >
          {t('activities.table.properties') || 'Properties'}
        </Button>
      ),
    },
  ]

  const propertiesDiffRows = useMemo(() => {
    const oldData = selectedActivity?.properties?.old ?? {}
    const newData = selectedActivity?.properties?.new ?? {}

    // Focus doctor formula_type price logs on base formula_type price comparison only.
    if (selectedActivity?.entity === 'ClientFormulaTypePrice') {
      const oldFt = oldData?.formula_type ?? oldData?.formula_type ?? {}
      const newFt = newData?.formula_type ?? newData?.formula_type ?? {}

      const focused = [
        {
          key: 'formula_type.selling_price',
          field: 'formula_type.selling_price',
          old: stringifyValue(oldFt?.selling_cost ?? oldFt?.selling_price ?? oldFt?.selling_price_per_unit),
          new: stringifyValue(newFt?.selling_cost ?? newFt?.selling_price ?? newFt?.selling_price_per_unit),
        },
        {
          key: 'formula_type.technician_cost',
          field: 'formula_type.technician_cost',
          old: stringifyValue(oldFt?.purchase_cost ?? oldFt?.technician_cost ?? oldFt?.purchase_price_per_unit),
          new: stringifyValue(newFt?.purchase_cost ?? newFt?.technician_cost ?? newFt?.purchase_price_per_unit),
        },
      ]

      return focused.filter((row) => row.old !== '-' || row.new !== '-')
    }

    const rows = diffObjects(oldData, newData, '', [], language)

    const compactOrderStatusRows = (inputRows) => {
      if (!Array.isArray(inputRows) || inputRows.length === 0) return inputRows

      const hasOrderStatusChanges = inputRows.some(
        (row) =>
          row.field === 'order_status_id' ||
          row.field === 'order_status' ||
          String(row.field || '').startsWith('order_status.')
      )
      if (!hasOrderStatusChanges) return inputRows

      const nameRow = inputRows.find((row) => row.field === 'order_status.name')
      const objectRow = inputRows.find((row) => row.field === 'order_status')
      const idRow = inputRows.find((row) => row.field === 'order_status_id')

      const oldStatusName =
        (nameRow?.old && String(nameRow.old) !== '-' ? nameRow.old : null) ??
        toNamedLabel(oldData?.order_status) ??
        (objectRow?.old && String(objectRow.old) !== '-' ? objectRow.old : null) ??
        (idRow?.old && String(idRow.old) !== '-' ? idRow.old : null) ??
        '-'

      const newStatusName =
        (nameRow?.new && String(nameRow.new) !== '-' ? nameRow.new : null) ??
        toNamedLabel(newData?.order_status) ??
        (objectRow?.new && String(objectRow.new) !== '-' ? objectRow.new : null) ??
        (idRow?.new && String(idRow.new) !== '-' ? idRow.new : null) ??
        '-'

      const filtered = inputRows.filter(
        (row) =>
          row.field !== 'order_status_id' &&
          row.field !== 'order_status' &&
          !String(row.field || '').startsWith('order_status.')
      )

      if (String(oldStatusName) === String(newStatusName)) {
        return filtered
      }

      return [
        ...filtered,
        {
          key: 'order_status',
          field: 'order_status',
          old: oldStatusName,
          new: newStatusName,
        },
      ]
    }

    const withoutVendorId = (inputRows) =>
      (Array.isArray(inputRows) ? inputRows : []).filter((row) => row.field !== 'vendor_id')

    const entity = String(selectedActivity?.entity ?? '').toLowerCase()
    const isDeliveryCenterEntity = entity === 'deliverycenter' || entity === 'delivery_center'
    if (isDeliveryCenterEntity) {
      const oldClientName = oldData?.client?.name ?? null
      const newClientName = newData?.client?.name ?? null

      return withoutVendorId(compactOrderStatusRows(rows
        .filter((row) => row.field !== 'id' && row.field !== 'client')
        .map((row) => {
          if (row.field !== 'client_id') return row
          return {
            ...row,
            key: 'client',
            field: 'client',
            old: oldClientName ?? '-',
            new: newClientName ?? '-',
          }
        })))
    }

    const isInvoiceEntity = entity === 'invoice'
    if (isInvoiceEntity) {
      const oldClientLabel = toNamedLabel(oldData?.client) ?? toNamedLabel(oldData?.client_name) ?? '-'
      const newClientLabel = toNamedLabel(newData?.client) ?? toNamedLabel(newData?.client_name) ?? '-'
      const oldPaymentMethodLabel = toNamedLabel(oldData?.payment_method) ?? '-'
      const newPaymentMethodLabel = toNamedLabel(newData?.payment_method) ?? '-'
      const oldPaymentStatusLabel = toNamedLabel(oldData?.payment_status) ?? '-'
      const newPaymentStatusLabel = toNamedLabel(newData?.payment_status) ?? '-'

      return withoutVendorId(compactOrderStatusRows(rows
        .filter(
          (row) =>
            row.field !== 'transaction' &&
            row.field !== 'payment_status_slug' &&
            row.field !== 'client' &&
            row.field !== 'payment_method' &&
            row.field !== 'payment_status'
        )
        .map((row) => {
          if (row.field === 'client_id') {
            return {
              ...row,
              key: 'client',
              field: 'client',
              old: oldClientLabel,
              new: newClientLabel,
            }
          }

          if (row.field === 'payment_method_id') {
            return {
              ...row,
              key: 'payment_method',
              field: 'payment_method',
              old: oldPaymentMethodLabel,
              new: newPaymentMethodLabel,
            }
          }

          if (row.field === 'payment_status_id') {
            return {
              ...row,
              key: 'payment_status',
              field: 'payment_status',
              old: oldPaymentStatusLabel,
              new: newPaymentStatusLabel,
            }
          }

          return row
        })))
    }

    const isOrderEntity = entity === 'order'
    if (isOrderEntity) {
      const oldClientLabel = toNamedLabel(oldData?.client) ?? toNamedLabel(oldData?.client_name) ?? '-'
      const newClientLabel = toNamedLabel(newData?.client) ?? toNamedLabel(newData?.client_name) ?? '-'
      const oldDoctorLabel = toNamedLabel(oldData?.doctor) ?? toNamedLabel(oldData?.doctor_name) ?? '-'
      const newDoctorLabel = toNamedLabel(newData?.doctor) ?? toNamedLabel(newData?.doctor_name) ?? '-'
      const oldProductLabel = toNamedLabel(oldData?.product) ?? '-'
      const newProductLabel = toNamedLabel(newData?.product) ?? '-'
      const oldDeliveryTypeLabel = oldData?.delivery_type ? translateDeliveryTypeName(oldData.delivery_type, language) : '-'
      const newDeliveryTypeLabel = newData?.delivery_type ? translateDeliveryTypeName(newData.delivery_type, language) : '-'
      const oldDeliveryCenterLabel = toNamedLabel(oldData?.delivery_center) ?? '-'
      const newDeliveryCenterLabel = toNamedLabel(newData?.delivery_center) ?? '-'
      const oldColorValue = formatDiffValue('color', oldData?.color ?? null, language)
      const newColorValue = formatDiffValue('color', newData?.color ?? null, language)
      const oldTechnicianLabel = toNamedLabel(oldData?.technician) ?? '-'
      const newTechnicianLabel = toNamedLabel(newData?.technician) ?? '-'
      const oldCourierLabel = toNamedLabel(oldData?.courier) ?? '-'
      const newCourierLabel = toNamedLabel(newData?.courier) ?? '-'

      const mapped = rows
        .map((row) => {
          if (row.field === 'client' || row.field === 'client_id') {
            return { ...row, key: 'client', field: 'client', old: oldClientLabel, new: newClientLabel }
          }
          if (row.field === 'doctor' || row.field === 'doctor_id') {
            return { ...row, key: 'doctor', field: 'doctor', old: oldDoctorLabel, new: newDoctorLabel }
          }
          if (row.field === 'product' || row.field === 'product_id') {
            return { ...row, key: 'product', field: 'product', old: oldProductLabel, new: newProductLabel }
          }
          if (row.field === 'delivery_type' || row.field === 'delivery_type_id') {
            return { ...row, key: 'delivery_type', field: 'delivery_type', old: oldDeliveryTypeLabel, new: newDeliveryTypeLabel }
          }
          if (row.field === 'delivery_center' || row.field === 'delivery_center_id') {
            return { ...row, key: 'delivery_center', field: 'delivery_center', old: oldDeliveryCenterLabel, new: newDeliveryCenterLabel }
          }
          if (row.field === 'color' || row.field === 'color_id') {
            return { ...row, key: 'color', field: 'color', old: oldColorValue, new: newColorValue }
          }
        
          if (row.field === 'technician' || row.field === 'technician_id') {
            return { ...row, key: 'technician', field: 'technician', old: oldTechnicianLabel, new: newTechnicianLabel }
          }
          if (row.field === 'courier' || row.field === 'courier_id') {
            return { ...row, key: 'courier', field: 'courier', old: oldCourierLabel, new: newCourierLabel }
          }
          return row
        })

      const deduped = []
      const seen = new Set()
      mapped.forEach((row) => {
        const key = String(row?.key ?? row?.field ?? '')
        if (!key || seen.has(key)) return
        seen.add(key)
        deduped.push(row)
      })

      return withoutVendorId(compactOrderStatusRows(deduped))
    }

    return withoutVendorId(compactOrderStatusRows(rows))
  }, [selectedActivity, language])

  const propertiesColumns = [
    {
      title: t('activities.properties.field') || 'Field',
      dataIndex: 'field',
      key: 'field',
      width: 260,
      render: (value) => {
        const normalized = toFieldTranslationKey(value)
        const translationKey = `activities.fields.${normalized}`
        const translated = t(translationKey)
        if (translated && translated !== translationKey) {
          return translated
        }
        return humanizeFieldLabel(value)
      },
    },
    {
      title: t('activities.properties.old') || 'Old',
      dataIndex: 'old',
      key: 'old',
      onCell: () => ({ style: { verticalAlign: 'top' } }),
      render: (value) => {
        if (value && typeof value === 'object' && value.__type === 'formula_type_details') {
          return (
            <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
              <tbody>
                <tr><td style={{ padding: '2px 6px', fontWeight: 600 }}>Name</td><td style={{ padding: '2px 6px' }}>{value.name}</td></tr>
                <tr><td style={{ padding: '2px 6px', fontWeight: 600 }}>Description</td><td style={{ padding: '2px 6px' }}>{value.description}</td></tr>
                <tr><td style={{ padding: '2px 6px', fontWeight: 600 }}>Selling Price</td><td style={{ padding: '2px 6px' }}>{value.sellingPrice}</td></tr>
                <tr><td style={{ padding: '2px 6px', fontWeight: 600 }}>Purchase Price</td><td style={{ padding: '2px 6px' }}>{value.purchasePrice}</td></tr>
              </tbody>
            </table>
          )
        }
        if (value && typeof value === 'object' && value.__type === 'color_display') {
          return (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              {value.code ? (
                <span
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: 3,
                    border: '1px solid #d9d9d9',
                    background: value.code,
                    display: 'inline-block',
                  }}
                />
              ) : null}
              <span>{value.name}</span>
              {value.code ? <span style={{ color: '#888' }}>({value.code})</span> : null}
            </div>
          )
        }
        if (value && typeof value === 'object' && value.__type === 'order_labels' && Array.isArray(value.items)) {
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {value.items.map((order) => (
                <span
                  key={`old-order-${order.key}`}
                  style={{
                    display: 'inline-block',
                    padding: '6px 10px',
                    borderRadius: 8,
                    background: '#f8f9fa',
                    color: '#495057',
                    border: '1px solid #dee2e6',
                    fontSize: 12,
                    lineHeight: 1.4,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {`Order: ${order.orderRef}\nClient: ${order.clientName}\nDoctor: ${order.doctorName}\nPatient: ${order.patientName}`}
                </span>
              ))}
            </div>
          )
        }
        if (value && typeof value === 'object' && value.__type === 'units_preview' && Array.isArray(value.cards)) {
          return (
            <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
              <div style={{ width: 289.61084, minWidth: 289.61084 }}>
                <ToothPicker
                  edit={false}
                  showLabel={false}
                  showArchButtons={false}
                  selected={value.selected}
                  width={289.61084}
                  height={370.54398}
                  unitCards={value.cards}
                  renderUnitCard={renderCompactUnitCard}
                />
              </div>
            </div>
          )
        }
        if (
          value &&
          typeof value === 'object' &&
          (value.__type === 'doctor_labels' || value.__type === 'attachment_labels' || value.__type === 'attachment_items') &&
          Array.isArray(value.items)
        ) {
          if (value.__type === 'attachment_labels' || value.__type === 'attachment_items') {
            return (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {value.items.map((item, idx) => {
                  const url = attachmentUrl(item?.path)
                  const name = item?.name ?? `Attachment ${idx + 1}`
                  const isImage = isImageAttachment(item)

                  if (isImage && url) {
                    return (
                      <div key={`old-att-${name}-${idx}`} style={{ width: 120, textAlign: 'center' }}>
                        <a href={url} target="_blank" rel="noreferrer">
                          <img src={url} alt={name} style={{ maxWidth: '100%', maxHeight: 90, display: 'block', marginBottom: 4 }} />
                          <div style={{ fontSize: 12, wordBreak: 'break-word' }}>{name}</div>
                        </a>
                      </div>
                    )
                  }

                  return (
                    <div key={`old-att-${name}-${idx}`} style={{ minWidth: 120 }}>
                      {url ? (
                        <a href={url} target="_blank" rel="noreferrer">{name}</a>
                      ) : (
                        <span>{name}</span>
                      )}
                    </div>
                  )
                })}
              </div>
            )
          }

          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {value.items.map((name) => (
                <span
                  key={`old-${name}`}
                  style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: 12,
                    background: '#e7f1ff',
                    color: '#084298',
                    border: '1px solid #b6d4fe',
                    fontSize: 12,
                    lineHeight: 1.4,
                  }}
                >
                  {name}
                </span>
              ))}
            </div>
          )
        }
        return <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{value}</pre>
      },
    },
    {
      title: t('activities.properties.new') || 'New',
      dataIndex: 'new',
      key: 'new',
      onCell: () => ({ style: { verticalAlign: 'top' } }),
      render: (value) => {
        if (value && typeof value === 'object' && value.__type === 'formula_type_details') {
          return (
            <table style={{ borderCollapse: 'collapse', fontSize: 12 }}>
              <tbody>
                <tr><td style={{ padding: '2px 6px', fontWeight: 600 }}>Name</td><td style={{ padding: '2px 6px' }}>{value.name}</td></tr>
                <tr><td style={{ padding: '2px 6px', fontWeight: 600 }}>Description</td><td style={{ padding: '2px 6px' }}>{value.description}</td></tr>
                <tr><td style={{ padding: '2px 6px', fontWeight: 600 }}>Selling Price</td><td style={{ padding: '2px 6px' }}>{value.sellingPrice}</td></tr>
                <tr><td style={{ padding: '2px 6px', fontWeight: 600 }}>Purchase Price</td><td style={{ padding: '2px 6px' }}>{value.purchasePrice}</td></tr>
              </tbody>
            </table>
          )
        }
        if (value && typeof value === 'object' && value.__type === 'color_display') {
          return (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              {value.code ? (
                <span
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: 3,
                    border: '1px solid #d9d9d9',
                    background: value.code,
                    display: 'inline-block',
                  }}
                />
              ) : null}
              <span>{value.name}</span>
              {value.code ? <span style={{ color: '#888' }}>({value.code})</span> : null}
            </div>
          )
        }
        if (value && typeof value === 'object' && value.__type === 'order_labels' && Array.isArray(value.items)) {
          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {value.items.map((order) => (
                <span
                  key={`new-order-${order.key}`}
                  style={{
                    display: 'inline-block',
                    padding: '6px 10px',
                    borderRadius: 8,
                    background: '#f8f9fa',
                    color: '#495057',
                    border: '1px solid #dee2e6',
                    fontSize: 12,
                    lineHeight: 1.4,
                    whiteSpace: 'pre-line',
                  }}
                >
                  {`Order: ${order.orderRef}\nClient: ${order.clientName}\nDoctor: ${order.doctorName}\nPatient: ${order.patientName}`}
                </span>
              ))}
            </div>
          )
        }
        if (value && typeof value === 'object' && value.__type === 'units_preview' && Array.isArray(value.cards)) {
          return (
            <div style={{ maxWidth: '100%', overflowX: 'auto' }}>
              <div style={{ width: 289.61084, minWidth: 289.61084 }}>
                <ToothPicker
                  edit={false}
                  showLabel={false}
                  showArchButtons={false}
                  selected={value.selected}
                  width={289.61084}
                  height={370.54398}
                  unitCards={value.cards}
                  renderUnitCard={renderCompactUnitCard}
                />
              </div>
            </div>
          )
        }
        if (
          value &&
          typeof value === 'object' &&
          (value.__type === 'doctor_labels' || value.__type === 'attachment_labels' || value.__type === 'attachment_items') &&
          Array.isArray(value.items)
        ) {
          if (value.__type === 'attachment_labels' || value.__type === 'attachment_items') {
            return (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                {value.items.map((item, idx) => {
                  const url = attachmentUrl(item?.path)
                  const name = item?.name ?? `Attachment ${idx + 1}`
                  const isImage = isImageAttachment(item)

                  if (isImage && url) {
                    return (
                      <div key={`new-att-${name}-${idx}`} style={{ width: 120, textAlign: 'center' }}>
                        <a href={url} target="_blank" rel="noreferrer">
                          <img src={url} alt={name} style={{ maxWidth: '100%', maxHeight: 90, display: 'block', marginBottom: 4 }} />
                          <div style={{ fontSize: 12, wordBreak: 'break-word' }}>{name}</div>
                        </a>
                      </div>
                    )
                  }

                  return (
                    <div key={`new-att-${name}-${idx}`} style={{ minWidth: 120 }}>
                      {url ? (
                        <a href={url} target="_blank" rel="noreferrer">{name}</a>
                      ) : (
                        <span>{name}</span>
                      )}
                    </div>
                  )
                })}
              </div>
            )
          }

          return (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {value.items.map((name) => (
                <span
                  key={`new-${name}`}
                  style={{
                    display: 'inline-block',
                    padding: '2px 8px',
                    borderRadius: 12,
                    background: '#e7f1ff',
                    color: '#084298',
                    border: '1px solid #b6d4fe',
                    fontSize: 12,
                    lineHeight: 1.4,
                  }}
                >
                  {name}
                </span>
              ))}
            </div>
          )
        }
        return <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{value}</pre>
      },
    },
  ]

  return (
    <div>
      {error ? <Alert type="error" showIcon message={error} style={{ marginBottom: 12 }} /> : null}

      <ListPageShell
        icon={<HistoryOutlined />}
        title={t('sidebar.activities')}
        subtitle={t('activities.subtitle') || 'Audit trail of user operations across the platform'}
        count={total}
        countLabel={t('sidebar.activities')}
        filters={
          <Row gutter={[12, 12]}>
            <Col xs={24} sm={12} md={6}>
              <Input
                allowClear
                placeholder={t('activities.filters.name') || 'Name'}
                value={filters.name}
                onChange={(e) => {
                  setCurrentPage(1)
                  setFilters((prev) => ({ ...prev, name: e.target.value }))
                }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder={t('activities.filters.entity') || 'Entity'}
                value={filters.entity || undefined}
                options={entityOptions}
                onChange={(value) => {
                  setCurrentPage(1)
                  setFilters((prev) => ({ ...prev, entity: value || '' }))
                }}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select
                allowClear
                style={{ width: '100%' }}
                placeholder={t('activities.filters.operation') || 'Operation'}
                value={filters.operation}
                options={[
                  { label: 'CREATE', value: 'create' },
                  { label: 'UPDATE', value: 'update' },
                  { label: 'DELETE', value: 'delete' },
                  { label: 'PAID_INVOICE', value: 'paid_invoice' },
                  { label: 'UNDO_MARK_PAID', value: 'undo_mark_paid' },
                  { label: 'REFUND_INVOICE', value: 'refund_invoice' },
                  { label: 'CANCEL', value: 'cancel' },
                  { label: 'SEND_EMAIL', value: 'send_email' },
                  { label: 'SEND_WHATSAPP', value: 'send_whatsapp' },
                  { label: 'DOWNLOAD_PDF', value: 'download_pdf' },
                  { label: 'LOGIN', value: 'login' },
                  { label: 'LOGOUT', value: 'logout' },
                  { label: 'PASSWORD RESET', value: 'password_reset' },
                  { label: 'PASSWORD RESET REQUEST', value: 'password_reset_request' },
                ]}
                onChange={(value) => {
                  setCurrentPage(1)
                  setFilters((prev) => ({ ...prev, operation: value }))
                }}
              />
            </Col>
            <Col xs={24} sm={12} md={6}>
              <DatePicker.RangePicker
                style={{ width: '100%' }}
                value={filters.dateRange}
                onChange={(dates) => {
                  setCurrentPage(1)
                  setFilters((prev) => ({ ...prev, dateRange: dates }))
                }}
              />
            </Col>
          </Row>
        }
        filtersActiveCount={
          [filters.name, filters.entity, filters.operation, filters.dateRange].filter(Boolean).length
        }
        onResetFilters={() => {
          setCurrentPage(1)
          setFilters({ name: '', entity: '', operation: undefined, dateRange: null })
        }}
      >
        <Table
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={rows}
          scroll={{ x: 720 }}
          pagination={{
            current: currentPage,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (page, size) => {
              setCurrentPage(page)
              setPageSize(size)
            },
          }}
        />
      </ListPageShell>

      <Modal
        open={propertiesModalOpen}
        title={t('activities.modal.properties_title') || 'Changed properties'}
        onCancel={() => {
          setPropertiesModalOpen(false)
          setSelectedActivity(null)
        }}
        footer={null}
        width="min(1000px, calc(100vw - 24px))"
        styles={{ body: { maxHeight: 'min(70vh, 640px)', overflow: 'auto' } }}
        destroyOnClose
      >
        {propertiesDiffRows.length === 0 ? (
          <Alert
            type="info"
            showIcon
            message={t('activities.properties.no_changes') || 'No old/new comparison data for this activity.'}
          />
        ) : (
          <Table
            rowKey="key"
            size="small"
            pagination={false}
            columns={propertiesColumns}
            dataSource={propertiesDiffRows}
            scroll={{ x: 900 }}
          />
        )}
      </Modal>
    </div>
  )
}

export default Activities
